<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.esc="cancel"
      @keydown.enter.prevent="handleEnter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Patch Builder</span>
        <v-spacer></v-spacer>
        <div class="d-flex align-center">
          <v-switch
              label="Smart Offers from root"
              v-model="transformSmartOffers"
              dense
              hide-details
              color="primary"
          ></v-switch>
        </div>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="formValid">
          <div class="mb-2">
            <strong>Current Config ID:</strong>
            <span class="text--secondary">{{ currConfigUID }}</span>
          </div>

          <v-autocomplete
              v-model="selectedConfig"
              :items="availableConfigs"
              :loading="isLoading"
              dense
              outlined
              item-text="name"
              item-value="uid"
              return-object
              hide-details
              label="Select base config to compare with"
              placeholder="Choose a config..."
              :rules="[v => !!v || 'Please select a config']"
          >
            <template #item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.uid }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <div v-if="errorMessage" class="ap-pb__error mt-2">
            {{ errorMessage }}
          </div>

          <v-row class="mt-2">
            <v-col class="col-12">
              <v-text-field
                  v-model="patchName"
                  label="Patch name"
                  required
                  v-ascii-only
                  :rules="[v => !!v || 'Patch name is required']"
              />
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col class="col-12">
              <v-autocomplete
                  v-model="selectedMapKinds"
                  :items="availableMapKinds"
                  :loading="isLoading"
                  :item-text="item => item.key + ' - ' + item.code"
                  item-value="code"
                  label="Select Map(s):"
                  return-object
                  multiple
                  small-chips
                  deletable-chips
                  :rules="mapKindsRules"
              >
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>Map: {{ item.key }} - Code: {{ item.code }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col class="col-12">
              <v-autocomplete
                  v-model="selectedMapGroups"
                  :items="availableMapGroups"
                  :loading="isLoading"
                  :item-text="item => item.key + ' - ' + item.code"
                  item-value="code"
                  label="Select Map Group(s):"
                  return-object
                  multiple
                  small-chips
                  deletable-chips
                  :rules="mapGroupsRules"
              >
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>Group: {{ item.key }} - Code: {{ item.code }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col class="col-12">
              <v-autocomplete
                  v-model="selectedMapExcluded"
                  :items="availableMapKinds"
                  :loading="isLoading"
                  :item-text="item => item.key + ' - ' + item.code"
                  item-value="code"
                  label="Select Map(s) to Exclude:"
                  return-object
                  multiple
                  small-chips
                  deletable-chips
              >
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>Map: {{ item.key }} - Code: {{ item.code }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row class="mt-0">
            <v-col class="col-8">
              <v-text-field
                  v-model="abTestId"
                  label="A/B Test ID"
                  required
                  v-ascii-only
                  :rules="abTestIdRules"
              />
            </v-col>
            <v-col>
              <v-autocomplete
                  v-model="abTestGroup"
                  :items="abTestGroups"
                  label="A/B Test Group"
                  required
                  hide-details
                  :rules="abTestGroupRules"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <div v-if="validationRan" class="ap-pb__valid">
          <div class="overline">Validation</div>
          <v-icon :class="validationError ? 'error--text' : 'success--text'">
            {{ validationError ? 'mdi-close-circle' : 'mdi-check-decagram' }}
          </v-icon>
        </div>

        <v-spacer></v-spacer>

        <v-btn text @click="cancel" :disabled="isLoading">
          Cancel
        </v-btn>
        <v-btn
            color="success"
            @click="buildPatch"
            :disabled="!canBuildPatch"
            :loading="isLoading"
        >
          Build Patch
        </v-btn>
      </v-card-actions>

      <div class="resize-handle" @mousedown="startResize"></div>
    </v-card>
  </v-dialog>
</template>

<script>
import { saveAs } from "file-saver";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import { compare, validate } from "fast-json-patch";

export default {
  name: "r-patch-builder-dialog",

  data() {
    return {
      dialog: false,
      formValid: false,
      selectedConfig: null,
      selectedMapKinds: [],
      selectedMapExcluded: [],
      selectedMapGroups: [],
      currConfigUID: RedactorUtils.getConfigUID(),
      availableConfigs: [],
      availableMapKinds: [],
      availableMapGroups: [],
      isLoading: false,
      errorMessage: null,
      validationRan: false,
      validationError: null,
      patchName: "",
      transformSmartOffers: false,
      abTestId: null,
      abTestGroup: null,
      abTestGroups: "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""),
      dialogWidth: 862,
      dialogHeight: 546,
      resizing: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 862,
      resizeStartHeight: 546,
      viewportPadding: 32
    };
  },

  computed: {
    canBuildPatch() {
      return !this.isLoading && this.formValid;
    },

    mapKindsRules() {
      const areBothMapIdsAndMapGroupsEmpty = this.selectedMapGroups.length <= 0 && this.selectedMapKinds.length <= 0;
      return [
        !areBothMapIdsAndMapGroupsEmpty || "Define at least one id or group",
      ];
    },

    mapGroupsRules() {
      const areBothMapIdsAndMapGroupsEmpty = this.selectedMapGroups.length <= 0 && this.selectedMapKinds.length <= 0;
      return [
        !areBothMapIdsAndMapGroupsEmpty || "Define at least one id or group",
      ];
    },

    abTestIdRules() {
      const snake = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

      return [
        v => !!v || "A/B Test ID is required",
        v => snake.test(v || "") || "Use snake_case (lowercase, no space)",
      ];
    },

    abTestGroupRules() {
      return [
        v => !!v || "A/B Test Group is required",
      ];
    },
  },

  watch: {
    dialog(isOpen) {
      if (isOpen) {
        this.loadConfigs();
        this.$nextTick(() => {
          this.$refs.form?.resetValidation();
        });
      } else {
        this.reset();
      }
    },

    mapId(newVal) {
      if (newVal !== null && newVal !== "") {
        const numValue = Number(newVal);

        if (numValue > 9999) {
          this.$nextTick(() => {
            this.mapId = 9999;
          });
        } else if (numValue < 0) {
          this.$nextTick(() => {
            this.mapId = 0;
          });
        }
      }
    }
  },

  methods: {
    show() {
      this.dialog = true;
    },

    async loadConfigs() {
      try {
        this.isLoading = true;
        this.availableConfigs = RedactorUtils.getGlobalConfigs() || [];
        this.availableMapGroups = RedactorUtils.getMapGroups() || [];
        this.availableMapKinds = RedactorUtils.getMapKinds() || [];
        this.errorMessage = null;
      } catch (error) {
        console.error("Failed to load configs:", error);
        this.errorMessage = "Failed to load configurations";
      } finally {
        this.isLoading = false;
      }
    },

    reset() {
      this.selectedConfig = null;
      this.errorMessage = null;
      this.isLoading = false;
      this.validationRan = false;
      this.validationError = null;
      this.patchName = "";
      this.mapId = null;
      this.abTestId = null;
      this.abTestGroup = null;
      this.formValid = false;
      this.$refs.form?.resetValidation();
      this.stopResize();
    },

    async buildPatch() {
      const isValid = await this.$refs.form.validate();
      if (!isValid || !this.canBuildPatch) return;

      try {
        this.isLoading = true;
        this.errorMessage = null;
        this.validationRan = false;
        this.validationError = null;

        const [baseConfig, currentConfig] = await Promise.all([
          RedactorUtils.getConfigData(this.selectedConfig.uid),
          RedactorUtils.getConfigData(this.currConfigUID)
        ]);

        if (!baseConfig || !currentConfig) {
          throw new Error("Failed to load configurations");
        }

        const patch = compare(baseConfig, currentConfig);

        if (this.isEmpty(patch)) {
          await this.$fire({
            title: "Patch not created",
            html: "Configurations are identical.",
            type: "info",
            width: "400px",
          });
          this.validationRan = false;

          return;
        }

        const err = validate(patch, baseConfig);
        this.validationRan = true;
        this.validationError = err || null;

        if (err) {
          console.error("JSON Patch validation failed:", err);
          return;
        }

        setTimeout(() => {
          this.downloadPatch(patch);
          this.dialog = false;
        }, 500);

      } catch (error) {
        console.error("Failed to build patch:", error);
        this.errorMessage = `Error: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },

    isEmpty(patch) {
      return !Array.isArray(patch) || patch.length === 0;
    },

    downloadPatch(patch) {
      // Transform smart-offers paths if enabled
      let finalPatch = patch;
      
      if (this.transformSmartOffers) {
        finalPatch = patch.map(operation => {
          // Remove /smart-offers/{id} prefix from path, keep the rest
          const smartOffersRegex = /^\/smart-offers\/[^/]+(.*)$/;
          const match = operation.path.match(smartOffersRegex);
          
          if (match) {
            return {
              ...operation,
              path: match[1] || "/"
            };
          }
          
          return operation;
        });
      }
        
      const oneOpPerLine = (ops) => {
        return ops.map(op => JSON.stringify(op)).join(",\n    ");
      };

      const patchData = {
        map_id: parseInt(this.mapId) || 0,
        map_ids: this.selectedMapKinds.map(v => parseInt(v.code)),
        map_groups: this.selectedMapGroups.map(v => parseInt(v.code)),
        map_excluded: this.selectedMapExcluded.map(v => parseInt(v.code)),
        name: this.patchName,
        ab_test_id: this.abTestId || "",
        ab_test_group: this.abTestGroup || "",
        patch_version: 2,
        import: [],
        data: finalPatch,
      };

      const content = [
        "{",
        patchData.map_ids.length > 0 ? `  "map_ids": [${patchData.map_ids.join(",")}],` : null,
        patchData.map_groups.length > 0 ? `  "map_groups": [${patchData.map_groups.join(",")}],` : null,
        patchData.map_excluded.length > 0 ? `  "map_excluded": [${patchData.map_excluded.join(",")}],` : null,
        `  "name": "${patchData.name}",`,
        `  "ab_test_id": "${patchData.ab_test_id}",`,
        `  "ab_test_group": "${patchData.ab_test_group}",`,
        `  "patch_version": ${patchData.patch_version},`,
        '  "import": [],',
        '  "data": [',
        "    " + oneOpPerLine(finalPatch),
        "  ]",
        "}",
      ].filter(v => v !== null).join("\n");

      const blob = new Blob([content], { type: "application/json" });
      const filename = `patch_${this.selectedConfig.uid}_to_${this.currConfigUID}.json`;

      saveAs(blob, filename);
    },

    cancel() {
      if (!this.isLoading) {
        this.dialog = false;
      }
    },

    handleEnter() {
      if (this.canBuildPatch) {
        this.buildPatch();
      } else {
        this.cancel();
      }
    },

    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },

    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 600;
      const minHeight = 360;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },

    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    }
  },

  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped lang="scss">
.ap-pb {
  &__valid {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 8px;
  }

  &__error {
    font-size: 0.875rem;
    color: #f44336;
  }
}

.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}

.resizable-card__actions {
  flex-shrink: 0;
}

.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}

.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>